import { CompanyTripDetailReportDtoPagingResponse, CompanyTripReportDto } from '@/__generated';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { dateTimeDisplayFormat, formatApiDate, ROW_GUTTER } from '@/config';
import { apiSdk } from '@/httpClient';
import MainContent from '@/layouts/MainContent';
import { Button, Col, Form, Input, Row, Select, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { t } from 'i18next';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import RouteInput from '@/components/FormItems/RouteInput';
import { exportService } from '@/services/exportService';
import { ExportExcelBtn, ExportPdfBtn } from '@/components/app/ExportBtn';

type Props = {};
const PAGE_TITLE = 'Trip Details Report';
const PageSize = 20;

const columns: ColumnsType<CompanyTripReportDto> = [
  { get title() { return t('Trip'); }, dataIndex: 'tripName', width: 250, render: v => <b>{v}</b>, fixed: 'left' },
  { get title() { return t('Bus'); }, dataIndex: 'busName', width: 200 },
  {
    get title() { return t('Departure Date'); },
    dataIndex: 'departureDate',
    width: 150,
    render: v => dateTimeDisplayFormat.dateOnly(v),
  },
  { get title() { return t('Distance'); }, dataIndex: 'distance', width: 150, render: v => `${v} kms` },

  { get title() { return t('No. of Passenger'); }, dataIndex: 'noOfPassenger' },
  { get title() { return t('No. of Shipment'); }, dataIndex: 'noOfShipment' },
  { get title() { return t('No. of Booking'); }, dataIndex: 'noOfBooking' },
  { get title() { return t('No. of Baggage'); }, dataIndex: 'noOfBaggage' },
];

const TripDetailsReport = (props: Props) => {
  const [searchForm] = Form.useForm();
  const [report, setReport] = React.useState<CompanyTripDetailReportDtoPagingResponse>();
  const [loading, setIsLoading] = React.useState(false);
  const [filter, setFilter] = React.useState<{ fromDate: string; toDate: string; pageIndex: number }>({
    fromDate: formatApiDate(moment().add(-1, 'month')),
    toDate: formatApiDate(moment()),
    pageIndex: 1,
  });

  React.useEffect(() => {
    setIsLoading(true);
    apiSdk.CompanyTripDetailReportService.getTripDetailReports({
      ...filter,
      pageSize: PageSize,
    })
      .then(res => {
        setReport(res);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const onSearch = ({ fromDate, toDate, ...props }: any) => {
    setFilter({
      ...props,
      fromDate: formatApiDate(fromDate),
      toDate: formatApiDate(toDate),
      pageIndex: 1,
    });
  };
  const onTableChange = pagination => {
    let p = pagination?.current || 1;
    setFilter(x => ({ ...x, pageIndex: p } as any));
  };

  const onExportExcel = () => {
    searchForm.validateFields().then(res => {
      exportService.exportCompanyTripDetailReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toExcel();
    });
  };
  const onExportPdf = () => {
    searchForm.validateFields().then(res => {
      exportService.exportCompanyTripDetailReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toPdf();
    });
  };
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        <Space>
          <ExportPdfBtn onClick={onExportPdf} />
          <ExportExcelBtn onClick={onExportExcel} />
        </Space>
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Form
          layout="inline"
          className="nta-custom-form"
          form={searchForm}
          colon={false}
          onFinish={onSearch}
          initialValues={filter}
          style={{ marginTop: 10 }}
        >
          <Row gutter={ROW_GUTTER}>
            <Col md={24} xl={24} xxl={24}>
              <Space>
                <Form.Item name="fromDate" label={t('From date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="toDate" label={t('To Date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="tripName" label={t('Trip Name')}>
                  <Input style={{ width: 250 }} allowClear={false} />
                </Form.Item>
                <Form.Item style={{ textAlign: 'left' }}>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined />
                      {t('zTable.search')}
                    </Button>
                    <Button type="ghost" htmlType="reset">
                      <CloseOutlined />
                      {t('zTable.reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>

        <Table
          className="transport-report-table"
          loading={loading}
          size="small"
          bordered
          columns={columns}
          dataSource={report?.data}
          pagination={{
            hideOnSinglePage: false,
            current: filter?.pageIndex || 1,
            pageSize: PageSize,
            total: report?.total,
            showSizeChanger: true,
            size: 'default',
            showTotal: total => <div>{`Total: ${total}`}</div>,
          }}
          onChange={onTableChange}
        />
      </div>
    </MainContent>
  );
};
export default TripDetailsReport;
